import React, { useState, useMemo, useCallback } from 'react';
import WaveformViewer from './WaveformViewer';
import djSetService, { TrackSegment, TrackVinylMatch } from '../services/djSetService';
import './TrackTimeline.css';

interface TrackTimelineProps {
  audioUrl: string;
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  tracks: TrackSegment[];
  onTrackClick?: (track: TrackSegment) => void;
  onDigDeeper?: (track: TrackSegment) => void;
  height?: number;
  waveformData?: number[];
  showTrackList?: boolean;
  showWaveform?: boolean;
  /**
   * Optional map of segmentId -> the best vinyl match for that track (TEM-635).
   * When a match exists for the currently-playing track, the current-track bar
   * shows the deep pressing/producer credits Record enriched into the shared
   * Qdrant `media` collection. Fully optional & defensive: callers that don't
   * supply it (or matches lacking these fields) simply render nothing extra.
   */
  vinylMatchBySegment?: Record<string, TrackVinylMatch>;
}

/**
 * Build a compact one-line pressing summary from a vinyl match, e.g.
 * "orig 1963 · 1980 pressing · Blue Note · BST-84003 · US".
 * Returns null when the match carries none of the pressing fields (defensive —
 * not every Qdrant point Record wrote includes pressing metadata).
 */
export function buildPressingSummary(match: TrackVinylMatch): string | null {
  const parts: string[] = [];
  if (match.originalYear) parts.push(`orig ${match.originalYear}`);
  if (match.pressYear) parts.push(`${match.pressYear} pressing`);
  if (match.label) parts.push(match.label);
  if (match.catalogNumber) parts.push(match.catalogNumber);
  if (match.country) parts.push(match.country);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * TrackTimeline - Waveform viewer with track boundary markers
 *
 * Extends WaveformViewer to show:
 * - Track boundaries as vertical markers
 * - Current track highlighting
 * - Track info tooltip on hover
 * - Click to seek to track start
 * - "Dig Deeper" button for per-track vinyl matching
 */
const TrackTimeline: React.FC<TrackTimelineProps> = ({
  audioUrl,
  currentTime,
  duration,
  onSeek,
  tracks,
  onTrackClick,
  onDigDeeper,
  height = 100,
  waveformData,
  showTrackList = true,
  showWaveform = true,
  vinylMatchBySegment,
}) => {
  const [hoveredTrack, setHoveredTrack] = useState<TrackSegment | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Convert currentTime (seconds) to milliseconds
  const currentTimeMs = currentTime * 1000;

  // Find the current track based on playback position
  const currentTrack = useMemo(() => {
    if (!tracks || tracks.length === 0) return null;
    return tracks.find(
      track => currentTimeMs >= track.startTimeMs && currentTimeMs < track.endTimeMs
    ) || null;
  }, [tracks, currentTimeMs]);

  // Deep pressing/producer credits for the current track, if a vinyl match is
  // available (TEM-635). Defensive: undefined when no map or no match.
  const currentVinylMatch = currentTrack
    ? vinylMatchBySegment?.[currentTrack.segmentId]
    : undefined;
  const currentPressingSummary = currentVinylMatch
    ? buildPressingSummary(currentVinylMatch)
    : null;

  // Calculate track marker positions (0-100%)
  const trackMarkers = useMemo(() => {
    if (!duration || duration === 0) return [];
    const durationMs = duration * 1000;
    return tracks.map(track => ({
      track,
      startPercent: (track.startTimeMs / durationMs) * 100,
      endPercent: (track.endTimeMs / durationMs) * 100,
      widthPercent: ((track.endTimeMs - track.startTimeMs) / durationMs) * 100,
    }));
  }, [tracks, duration]);

  // Handle clicking on a track marker
  const handleMarkerClick = useCallback((track: TrackSegment, e: React.MouseEvent) => {
    e.stopPropagation();
    // Seek to start of track
    onSeek(track.startTimeMs / 1000);
    onTrackClick?.(track);
  }, [onSeek, onTrackClick]);

  // Handle hover on track marker
  const handleMarkerHover = useCallback((track: TrackSegment | null, e?: React.MouseEvent) => {
    setHoveredTrack(track);
    if (e && track) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  }, []);

  // Handle clicking track in list
  const handleTrackListClick = useCallback((track: TrackSegment) => {
    onSeek(track.startTimeMs / 1000);
    onTrackClick?.(track);
  }, [onSeek, onTrackClick]);

  // Get boundary type color
  const getBoundaryColor = (type: string): string => {
    switch (type) {
      case 'silence': return 'rgba(34, 197, 94, 0.8)'; // Green - clean break
      case 'gapless': return 'rgba(59, 130, 246, 0.8)'; // Blue - beat-matched
      case 'transition': return 'rgba(168, 85, 247, 0.8)'; // Purple - crossfade
      default: return 'rgba(100, 116, 139, 0.8)';
    }
  };

  return (
    <div className="track-timeline">
      {/* Waveform with track overlay */}
      {showWaveform && (
      <div className="track-timeline-waveform">
        <WaveformViewer
          audioUrl={audioUrl}
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
          height={height}
          waveformData={waveformData}
        />

        {/* Track markers overlay */}
        <div className="track-markers-overlay">
          {trackMarkers.map(({ track, startPercent, widthPercent }) => (
            <div
              key={track.segmentId}
              className={`track-marker ${currentTrack?.segmentId === track.segmentId ? 'current' : ''}`}
              style={{
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
                borderLeftColor: getBoundaryColor(track.boundaryType),
              }}
              onClick={(e) => handleMarkerClick(track, e)}
              onMouseEnter={(e) => handleMarkerHover(track, e)}
              onMouseLeave={() => handleMarkerHover(null)}
              title={track.title || `Track ${track.trackNumber}`}
            >
              {/* Track number indicator */}
              <span className="track-number">{track.trackNumber}</span>
            </div>
          ))}
        </div>

        {/* Tooltip */}
        {hoveredTrack && (
          <div
            className="track-tooltip"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
            }}
          >
            <div className="tooltip-title">
              {hoveredTrack.recognized ? (
                <>
                  <span className="tooltip-track-name">{hoveredTrack.title || 'Unknown'}</span>
                  {hoveredTrack.artist && (
                    <span className="tooltip-artist">{hoveredTrack.artist}</span>
                  )}
                </>
              ) : (
                <span className="tooltip-track-name">Unidentified Track</span>
              )}
            </div>
            <div className="tooltip-meta">
              <span className="tooltip-time">
                {djSetService.formatPosition(hoveredTrack.startTimeMs)} - {djSetService.formatPosition(hoveredTrack.endTimeMs)}
              </span>
              <span className={`tooltip-boundary boundary-${hoveredTrack.boundaryType}`}>
                {hoveredTrack.boundaryType}
              </span>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Current track info bar */}
      {currentTrack && (
        <div className="current-track-bar">
          <div className="current-track-info">
            <span className="current-track-label">Now Playing</span>
            <span className="current-track-number">Track {currentTrack.trackNumber}</span>
            {currentTrack.recognized ? (
              <>
                <span className="current-track-title">{currentTrack.title || 'Unknown'}</span>
                {currentTrack.artist && (
                  <span className="current-track-artist">by {currentTrack.artist}</span>
                )}
              </>
            ) : (
              <span className="current-track-title unidentified">Unidentified Track</span>
            )}
          </div>
          {/* Deep pressing / producer credits (TEM-635) — render each chip only
              when its field is present on the current track's vinyl match. */}
          {currentVinylMatch && (currentVinylMatch.producer || currentVinylMatch.engineer || currentVinylMatch.masteredBy || currentPressingSummary) && (
            <div className="current-track-credits">
              {currentVinylMatch.producer && (
                <span className="credit-chip credit-producer">Producer: {currentVinylMatch.producer}</span>
              )}
              {currentVinylMatch.engineer && (
                <span className="credit-chip credit-engineer">Engineer: {currentVinylMatch.engineer}</span>
              )}
              {currentVinylMatch.masteredBy && (
                <span className="credit-chip credit-mastered">Mastered: {currentVinylMatch.masteredBy}</span>
              )}
              {currentPressingSummary && (
                <span className="credit-chip credit-pressing">({currentPressingSummary})</span>
              )}
            </div>
          )}
          {onDigDeeper && (
            <button
              className="dig-deeper-btn"
              onClick={() => onDigDeeper(currentTrack)}
              title="Find this track in your vinyl collection"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2"/>
                <circle cx="10" cy="10" r="2" fill="currentColor"/>
                <path d="M10 3v1M10 16v1M3 10h1M16 10h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Dig Deeper
            </button>
          )}
        </div>
      )}

      {/* Track list sidebar (optional) */}
      {showTrackList && tracks.length > 0 && (
        <div className="track-list">
          <h4 className="track-list-header">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M4 4h12M4 8h12M4 12h8M4 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Tracklist ({tracks.length} tracks)
          </h4>
          <ul className="track-list-items">
            {tracks.map((track) => (
              <li
                key={track.segmentId}
                className={`track-list-item ${currentTrack?.segmentId === track.segmentId ? 'playing' : ''}`}
                onClick={() => handleTrackListClick(track)}
              >
                <span className="track-list-number">{track.trackNumber}</span>
                <div className="track-list-info">
                  {track.recognized ? (
                    <>
                      <span className="track-list-title">{track.title || 'Unknown'}</span>
                      {track.artist && (
                        <span className="track-list-artist">{track.artist}</span>
                      )}
                    </>
                  ) : (
                    <span className="track-list-title unidentified">Unidentified Track</span>
                  )}
                  <span className="track-list-time">
                    {djSetService.formatDuration(track.durationMs)}
                  </span>
                </div>
                {currentTrack?.segmentId === track.segmentId && (
                  <span className="playing-indicator">
                    <span className="bar"></span>
                    <span className="bar"></span>
                    <span className="bar"></span>
                  </span>
                )}
                {onDigDeeper && (
                  <button
                    className="track-dig-deeper-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDigDeeper(track);
                    }}
                    title="Find in your vinyl collection"
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="6" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TrackTimeline;
